import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Sparkles, Thermometer } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Seo } from '../components/Seo';
import { LEGAL_ORG_NAME } from '../lib/legal-site-info';
import { REVIEW_HIGHLIGHTS, REVIEWS } from '../lib/reviewsData';

const HIGH_ICONS = [Thermometer, MessageCircle, Sparkles] as const;

function ReviewAvatar({ url, initials }: { url: string; initials: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ct-accent/20 text-sm font-bold text-ct-accent ring-2 ring-ct-border"
        aria-hidden
      >
        {initials}
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      width={56}
      height={56}
      className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-ct-border"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

export function ReviewsPage() {
  return (
    <>
      <Seo
        path="/reviews"
        title="Reviews en ervaringen met ComfortTool™"
        description={`Lees hoe bewoners, VvE’s en professionals ComfortTool™ van ${LEGAL_ORG_NAME} gebruiken voor indicatieve warmte- en koelinzichten.`}
        keywords="ComfortTool reviews, ervaringen, warmte koel scan, installateur, adviseur, VvE, particulier"
      />
      <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:py-14">
        <div className="mx-auto max-w-3xl">
          <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Reviews' }]} />
        </div>

        <header className="mx-auto mt-6 max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-ct-border bg-ct-surface px-3 py-1.5 text-xs font-semibold text-ct-muted">
            <Sparkles className="h-3.5 w-3.5 text-ct-accent" aria-hidden />
            Ervaringen
          </div>
          <h1 className="mt-4 text-balance text-3xl font-bold tracking-tight text-ct-text sm:text-4xl md:text-[2.5rem]">
            Wat anderen zeggen
          </h1>
          <p className="mt-4 text-pretty text-base leading-relaxed text-ct-muted sm:text-lg">
            Een selectie reacties — kort en concreet. Geen twee situaties zijn hetzelfde; jouw uitkomst kan anders zijn.
          </p>
        </header>

        <section className="mt-10 w-full" aria-labelledby="reviews-highlights-heading">
          <h2 id="reviews-highlights-heading" className="sr-only">
            Pijlers
          </h2>
          <ul className="mx-auto grid max-w-5xl grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-5">
            {REVIEW_HIGHLIGHTS.map((h, i) => {
              const Icon = HIGH_ICONS[i] ?? Sparkles;
              return (
                <li
                  key={h.title}
                  className="flex gap-3 rounded-2xl border border-ct-border bg-ct-surface/80 p-5 sm:flex-col sm:p-6"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ct-accent/15 text-ct-accent">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-ct-text sm:text-base">{h.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-ct-muted">{h.text}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="mt-14 w-full" aria-labelledby="reviews-cards-heading">
          <div className="mx-auto mb-6 max-w-3xl">
            <h2 id="reviews-cards-heading" className="text-xl font-bold text-ct-text sm:text-2xl">
              Reacties
            </h2>
          </div>
          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
            {REVIEWS.map((r) => (
              <article
                key={r.id}
                className="flex min-w-0 flex-col rounded-2xl border border-ct-border bg-ct-surface/90 p-6 shadow-sm transition hover:border-ct-accent/25 hover:shadow-lg"
              >
                <blockquote className="min-w-0 flex-1 border-l-4 border-ct-accent/35 pl-4 text-sm italic leading-relaxed text-ct-muted sm:text-base">
                  {r.quote}
                </blockquote>
                <footer className="mt-6 flex items-center gap-3 border-t border-ct-border pt-6">
                  <ReviewAvatar url={r.avatarUrl} initials={r.initials} />
                  <div className="min-w-0">
                    <p className="font-semibold text-ct-text">{r.name}</p>
                    <p className="text-sm text-ct-muted">{r.role}</p>
                  </div>
                </footer>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-14 max-w-3xl rounded-2xl border border-dashed border-ct-border bg-ct-elevated/30 px-6 py-6 text-center">
          <h2 className="text-base font-bold text-ct-text">Zelf ervaren?</h2>
          <p className="mt-2 text-sm text-ct-muted">Doorloop de scan en bekijk je indicatieve uitkomst.</p>
          <Link
            to="/scan"
            className="mt-4 inline-flex rounded-xl bg-ct-accent px-6 py-3 text-sm font-bold text-white hover:bg-blue-500"
          >
            Start de scan
          </Link>
        </section>
      </div>
    </>
  );
}
